<template>
  <AppLayout 
    page-title="Expenses" 
    page-subtitle="Track business expenses, receipts, and mileage"
  >
    <!-- Page Header -->
    <div class="mb-8 pt-8">
      <div class="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-6">
        <span class="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
        Expense Management
      </div>
      <h1 class="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">Expenses</h1>
      <p class="text-xl text-slate-300 mb-8 max-w-2xl">Track business expenses, upload receipts, and manage mileage for tax deductions</p>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <div class="text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-400 mx-auto mb-4"></div>
        <p class="text-slate-400">Loading expense data...</p>
      </div>
    </div>

    <!-- Expense Overview -->
    <div v-else class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
      <div class="expense-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">This Month</p>
          <p class="text-3xl font-bold text-white">${{ expenseStats.thisMonth.toLocaleString() }}</p>
          <p class="text-sm text-emerald-400">total expenses</p>
        </div>
      </div>

      <div class="expense-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-blue-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">This Year</p>
          <p class="text-3xl font-bold text-white">${{ expenseStats.thisYear.toLocaleString() }}</p>
          <p class="text-sm text-blue-400">total expenses</p>
        </div>
      </div>

      <div class="expense-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-amber-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">Mileage</p>
          <p class="text-3xl font-bold text-white">{{ expenseStats.totalMileage.toLocaleString() }}</p>
          <p class="text-sm text-amber-400">miles tracked</p>
        </div>
      </div>

      <div class="expense-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">Receipts</p>
          <p class="text-3xl font-bold text-white">{{ expenseStats.totalReceipts }}</p>
          <p class="text-sm text-purple-400">uploaded</p>
        </div>
      </div>
    </div>

    <!-- Quick Actions -->
    <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 mb-8">
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-2xl font-bold text-white">Quick Actions</h2>
        <div class="flex space-x-3">
          <button @click="showAddExpenseModal = true" class="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors">
            Add Expense
          </button>
          <button @click="showMileageModal = true" class="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors">
            Log Mileage
          </button>
          <button @click="uploadReceipt" class="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white font-medium rounded-lg transition-colors">
            Upload Receipt
          </button>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div class="text-center p-4 bg-slate-700/30 rounded-lg">
          <div class="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
            <svg class="w-6 h-6 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"/>
            </svg>
          </div>
          <h3 class="text-white font-medium mb-1">Add Expense</h3>
          <p class="text-sm text-slate-400">Manually add business expense</p>
        </div>

        <div class="text-center p-4 bg-slate-700/30 rounded-lg">
          <div class="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
            <svg class="w-6 h-6 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"/>
            </svg>
          </div>
          <h3 class="text-white font-medium mb-1">Log Mileage</h3>
          <p class="text-sm text-slate-400">Track business mileage</p>
        </div>

        <div class="text-center p-4 bg-slate-700/30 rounded-lg">
          <div class="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
            <svg class="w-6 h-6 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clip-rule="evenodd"/>
            </svg>
          </div>
          <h3 class="text-white font-medium mb-1">Upload Receipt</h3>
          <p class="text-sm text-slate-400">Scan and upload receipts</p>
        </div>
      </div>
    </div>

    <!-- Expense Categories -->
    <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 mb-8">
      <h2 class="text-2xl font-bold text-white mb-6">Expense Categories</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div v-for="category in expenseCategories" :key="category.id" class="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
          <div class="flex items-center space-x-3">
            <div :class="`w-10 h-10 ${category.color} rounded-lg flex items-center justify-center`">
              <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path :d="category.icon" fill-rule="evenodd" clip-rule="evenodd"/>
              </svg>
            </div>
            <div>
              <p class="text-white font-medium">{{ category.name }}</p>
              <p class="text-sm text-slate-400">{{ category.count }} expenses</p>
            </div>
          </div>
          <div class="text-right">
            <p class="text-white font-bold">${{ category.total.toLocaleString() }}</p>
            <p class="text-sm text-slate-400">{{ category.percentage }}%</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Recent Expenses -->
    <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 mb-8">
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-2xl font-bold text-white">Recent Expenses</h2>
        <button @click="showExpenseHistoryModal = true" class="px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-white font-medium rounded-lg transition-colors">
          View All
        </button>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr class="border-b border-slate-700/50">
              <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Date</th>
              <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Description</th>
              <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Category</th>
              <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Amount</th>
              <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Receipt</th>
              <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="expense in recentExpenses" :key="expense.id" class="border-b border-slate-700/30 hover:bg-slate-700/20 transition-colors">
              <td class="py-4 px-4">
                <p class="text-white">{{ formatDate(expense.date) }}</p>
              </td>
              <td class="py-4 px-4">
                <p class="text-white font-medium">{{ expense.description }}</p>
                <p class="text-sm text-slate-400">{{ expense.vendor }}</p>
              </td>
              <td class="py-4 px-4">
                <span :class="getCategoryClass(expense.category)" class="px-3 py-1 rounded-full text-xs font-medium">
                  {{ expense.category }}
                </span>
              </td>
              <td class="py-4 px-4">
                <p class="text-white font-bold">${{ expense.amount.toLocaleString() }}</p>
              </td>
              <td class="py-4 px-4">
                <button v-if="expense.receiptUrl" @click="viewReceipt(expense)" class="text-blue-400 hover:text-blue-300 transition-colors">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                  </svg>
                </button>
                <span v-else class="text-slate-400">No receipt</span>
              </td>
              <td class="py-4 px-4">
                <div class="flex space-x-2">
                  <button @click="editExpense(expense)" class="text-emerald-400 hover:text-emerald-300 transition-colors">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                    </svg>
                  </button>
                  <button @click="deleteExpense(expense)" class="text-red-400 hover:text-red-300 transition-colors">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Mileage Tracking -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
        <h3 class="text-xl font-bold text-white mb-4">Mileage Tracking</h3>
        <div class="space-y-4">
          <div v-for="mileage in recentMileage" :key="mileage.id" class="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
            <div>
              <p class="text-white font-medium">{{ mileage.description }}</p>
              <p class="text-sm text-slate-400">{{ formatDate(mileage.date) }} • {{ mileage.miles }} miles</p>
            </div>
            <div class="text-right">
              <p class="text-white font-bold">${{ mileage.reimbursement.toLocaleString() }}</p>
              <p class="text-sm text-slate-400">{{ mileage.rate }}¢/mile</p>
            </div>
          </div>
        </div>
      </div>

      <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
        <h3 class="text-xl font-bold text-white mb-4">Expense Reports</h3>
        <div class="space-y-4">
          <div v-for="report in expenseReports" :key="report.id" class="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
            <div>
              <p class="text-white font-medium">{{ report.name }}</p>
              <p class="text-sm text-slate-400">{{ report.period }} • {{ report.expenseCount }} expenses</p>
            </div>
            <div class="text-right">
              <p class="text-white font-bold">${{ report.total.toLocaleString() }}</p>
              <span :class="getReportStatusClass(report.status)" class="px-2 py-1 rounded-full text-xs font-medium">
                {{ report.status }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Expense Modal -->
    <div v-if="showAddExpenseModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div class="bg-slate-800/95 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 w-full max-w-md">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xl font-bold text-white">Add Expense</h3>
          <button @click="showAddExpenseModal = false" class="text-slate-400 hover:text-white">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        
        <form @submit.prevent="addExpense" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Date</label>
            <input v-model="expenseForm.date" type="date" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Description</label>
            <input v-model="expenseForm.description" type="text" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white" placeholder="e.g., Fuel for work truck">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Vendor</label>
            <input v-model="expenseForm.vendor" type="text" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white" placeholder="e.g., Shell Gas Station">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Category</label>
            <select v-model="expenseForm.category" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
              <option value="fuel">Fuel</option>
              <option value="equipment">Equipment</option>
              <option value="meals">Meals & Per Diem</option>
              <option value="lodging">Lodging</option>
              <option value="tools">Tools & Supplies</option>
              <option value="insurance">Insurance</option>
              <option value="other">Other</option>
            </select>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Amount</label>
            <input v-model.number="expenseForm.amount" type="number" min="0" step="0.01" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white" placeholder="0.00">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Notes</label>
            <textarea v-model="expenseForm.notes" rows="3" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white" placeholder="Optional notes..."></textarea>
          </div>
          
          <div class="flex space-x-3">
            <button type="button" @click="showAddExpenseModal = false" class="flex-1 px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-white rounded-lg">
              Cancel
            </button>
            <button type="submit" class="flex-1 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg">
              Add Expense
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Mileage Modal -->
    <div v-if="showMileageModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div class="bg-slate-800/95 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 w-full max-w-md">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xl font-bold text-white">Log Mileage</h3>
          <button @click="showMileageModal = false" class="text-slate-400 hover:text-white">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        
        <form @submit.prevent="addMileage" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Date</label>
            <input v-model="mileageForm.date" type="date" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Description</label>
            <input v-model="mileageForm.description" type="text" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white" placeholder="e.g., Travel to job site">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Miles</label>
            <input v-model.number="mileageForm.miles" type="number" min="0" step="0.1" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white" placeholder="0.0">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Rate per Mile</label>
            <input v-model.number="mileageForm.rate" type="number" min="0" step="0.01" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white" placeholder="0.65">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Purpose</label>
            <select v-model="mileageForm.purpose" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
              <option value="business">Business Travel</option>
              <option value="client">Client Meeting</option>
              <option value="supplies">Supplies Run</option>
              <option value="other">Other</option>
            </select>
          </div>
          
          <div class="flex space-x-3">
            <button type="button" @click="showMileageModal = false" class="flex-1 px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-white rounded-lg">
              Cancel
            </button>
            <button type="submit" class="flex-1 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg">
              Log Mileage
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Expense History Modal -->
    <div v-if="showExpenseHistoryModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div class="bg-slate-800/95 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xl font-bold text-white">Expense History</h3>
          <button @click="showExpenseHistoryModal = false" class="text-slate-400 hover:text-white">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr class="border-b border-slate-700/50">
                <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Date</th>
                <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Description</th>
                <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Category</th>
                <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Amount</th>
                <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="expense in allExpenses" :key="expense.id" class="border-b border-slate-700/30 hover:bg-slate-700/20 transition-colors">
                <td class="py-4 px-4">
                  <p class="text-white">{{ formatDate(expense.date) }}</p>
                </td>
                <td class="py-4 px-4">
                  <p class="text-white font-medium">{{ expense.description }}</p>
                  <p class="text-sm text-slate-400">{{ expense.vendor }}</p>
                </td>
                <td class="py-4 px-4">
                  <span :class="getCategoryClass(expense.category)" class="px-3 py-1 rounded-full text-xs font-medium">
                    {{ expense.category }}
                  </span>
                </td>
                <td class="py-4 px-4">
                  <p class="text-white font-bold">${{ expense.amount.toLocaleString() }}</p>
                </td>
                <td class="py-4 px-4">
                  <span :class="getExpenseStatusClass(expense.status)" class="px-3 py-1 rounded-full text-xs font-medium">
                    {{ expense.status }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import AppLayout from '@/components/layout/AppLayout.vue'
import { useUserStore } from '@/stores/userStore'
import { onMounted, ref } from 'vue'

const userStore = useUserStore()

// Types
interface ExpenseStats {
  thisMonth: number
  thisYear: number
  totalMileage: number
  totalReceipts: number
}

interface ExpenseCategory {
  id: number
  name: string
  color: string
  icon: string
  count: number
  total: number
  percentage: number
}

interface Expense {
  id: number
  date: string
  description: string
  vendor: string
  category: string
  amount: number
  receiptUrl?: string
  status: string
}

interface MileageEntry {
  id: number
  date: string
  description: string
  miles: number
  rate: number
  reimbursement: number
}

interface ExpenseReport {
  id: number
  name: string
  period: string
  expenseCount: number
  total: number
  status: string
}

// Reactive data
const isLoading = ref(true)
const showAddExpenseModal = ref(false)
const showMileageModal = ref(false)
const showExpenseHistoryModal = ref(false)

const expenseStats = ref<ExpenseStats>({
  thisMonth: 0,
  thisYear: 0,
  totalMileage: 0,
  totalReceipts: 0
})

const expenseCategories = ref<ExpenseCategory[]>([])
const recentExpenses = ref<Expense[]>([])
const allExpenses = ref<Expense[]>([])
const recentMileage = ref<MileageEntry[]>([])
const expenseReports = ref<ExpenseReport[]>([])

// Form data
const expenseForm = ref({
  date: '',
  description: '',
  vendor: '',
  category: 'fuel',
  amount: 0,
  notes: ''
})

const mileageForm = ref({
  date: '',
  description: '',
  miles: 0,
  rate: 0.65,
  purpose: 'business'
})

// API Methods
const fetchExpenseData = async () => {
  try {
    isLoading.value = true
    
    // Fetch expense statistics
    const statsResponse = await fetch('/api/expenses/stats', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (statsResponse.ok) {
      const statsData = await statsResponse.json()
      expenseStats.value = statsData
    }
    
    // Fetch expense categories
    const categoriesResponse = await fetch('/api/expenses/categories', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (categoriesResponse.ok) {
      const categoriesData = await categoriesResponse.json()
      expenseCategories.value = categoriesData
    }
    
    // Fetch recent expenses
    const expensesResponse = await fetch('/api/expenses/recent', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (expensesResponse.ok) {
      const expensesData = await expensesResponse.json()
      recentExpenses.value = expensesData
    }
    
    // Fetch all expenses
    const allExpensesResponse = await fetch('/api/expenses/all', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (allExpensesResponse.ok) {
      const allExpensesData = await allExpensesResponse.json()
      allExpenses.value = allExpensesData
    }
    
    // Fetch mileage entries
    const mileageResponse = await fetch('/api/expenses/mileage', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (mileageResponse.ok) {
      const mileageData = await mileageResponse.json()
      recentMileage.value = mileageData
    }
    
    // Fetch expense reports
    const reportsResponse = await fetch('/api/expenses/reports', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (reportsResponse.ok) {
      const reportsData = await reportsResponse.json()
      expenseReports.value = reportsData
    }
    
  } catch (error) {
    console.error('Error fetching expense data:', error)
    // Fallback to empty data
    expenseStats.value = {
      thisMonth: 0,
      thisYear: 0,
      totalMileage: 0,
      totalReceipts: 0
    }
    expenseCategories.value = []
    recentExpenses.value = []
    allExpenses.value = []
    recentMileage.value = []
    expenseReports.value = []
  } finally {
    isLoading.value = false
  }
}

// Methods
const addExpense = async () => {
  try {
    const response = await fetch('/api/expenses/add', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      },
      body: JSON.stringify(expenseForm.value)
    })
    
    if (response.ok) {
      alert('Expense added successfully!')
      showAddExpenseModal.value = false
      // Reset form
      expenseForm.value = {
        date: '',
        description: '',
        vendor: '',
        category: 'fuel',
        amount: 0,
        notes: ''
      }
      await fetchExpenseData() // Refresh data
    } else {
      alert('Error adding expense')
    }
  } catch (error) {
    console.error('Error adding expense:', error)
    alert('Error adding expense')
  }
}

const addMileage = async () => {
  try {
    const response = await fetch('/api/expenses/mileage/add', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      },
      body: JSON.stringify(mileageForm.value)
    })
    
    if (response.ok) {
      alert('Mileage logged successfully!')
      showMileageModal.value = false
      // Reset form
      mileageForm.value = {
        date: '',
        description: '',
        miles: 0,
        rate: 0.65,
        purpose: 'business'
      }
      await fetchExpenseData() // Refresh data
    } else {
      alert('Error logging mileage')
    }
  } catch (error) {
    console.error('Error logging mileage:', error)
    alert('Error logging mileage')
  }
}

const uploadReceipt = () => {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = 'image/*,.pdf'
  input.onchange = async (event) => {
    const file = (event.target as HTMLInputElement).files?.[0]
    if (file) {
      try {
        const formData = new FormData()
        formData.append('receipt', file)
        
        const response = await fetch('/api/expenses/upload-receipt', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${userStore.authToken}`
          },
          body: formData
        })
        
        if (response.ok) {
          alert('Receipt uploaded successfully!')
          await fetchExpenseData() // Refresh data
        } else {
          alert('Error uploading receipt')
        }
      } catch (error) {
        console.error('Error uploading receipt:', error)
        alert('Error uploading receipt')
      }
    }
  }
  input.click()
}

const editExpense = (expense: Expense) => {
  console.log('Editing expense:', expense)
  alert(`Edit expense: ${expense.description}`)
}

const deleteExpense = async (expense: Expense) => {
  if (confirm(`Are you sure you want to delete "${expense.description}"?`)) {
    try {
      const response = await fetch(`/api/expenses/delete/${expense.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userStore.authToken}`
        }
      })
      
      if (response.ok) {
        alert('Expense deleted successfully!')
        await fetchExpenseData() // Refresh data
      } else {
        alert('Error deleting expense')
      }
    } catch (error) {
      console.error('Error deleting expense:', error)
      alert('Error deleting expense')
    }
  }
}

const viewReceipt = (expense: Expense) => {
  if (expense.receiptUrl) {
    window.open(expense.receiptUrl, '_blank')
  }
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

const getCategoryClass = (category: string) => {
  const classes: { [key: string]: string } = {
    'fuel': 'bg-red-500/90',
    'equipment': 'bg-blue-500/90',
    'meals': 'bg-green-500/90',
    'lodging': 'bg-purple-500/90',
    'tools': 'bg-amber-500/90',
    'insurance': 'bg-cyan-500/90',
    'other': 'bg-slate-500/90'
  }
  return classes[category] || 'bg-slate-500/90'
}

const getExpenseStatusClass = (status: string) => {
  const classes: { [key: string]: string } = {
    'Approved': 'bg-emerald-500/90',
    'Pending': 'bg-yellow-500/90',
    'Rejected': 'bg-red-500/90',
    'Submitted': 'bg-blue-500/90'
  }
  return classes[status] || 'bg-slate-500/90'
}

const getReportStatusClass = (status: string) => {
  const classes: { [key: string]: string } = {
    'Approved': 'bg-emerald-500/90',
    'Pending': 'bg-yellow-500/90',
    'Rejected': 'bg-red-500/90',
    'Draft': 'bg-slate-500/90'
  }
  return classes[status] || 'bg-slate-500/90'
}

// Load data on mount
onMounted(() => {
  fetchExpenseData()
})
</script>

<style scoped>
.expense-stat-card {
  @apply bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 hover:border-emerald-500/50 transition-all duration-300;
}
</style>
